<template>
    <div class="h-full flex flex-col bg-white font-oceanside">
        <div class="sticky top-0 z-50 bg-white w-full h-[88px] border-b border-gray-DFDFDF px-[20px] lg:px-[40px] flex items-center justify-center">
            <h2 class="font-semibold leading-none font-oceanside">
                {{ $t('COLOR_CHANGE_CONFIRM_TITLE') }}
            </h2>
        </div>

        <div class="px-[20px] lg:px-[40px] py-[16px] flex-1 overflow-y-auto">
            <div class="text-center font-oceanside border-b border-gray-DFDFDF pb-[16px] -mx-[20px] lg:-mx-[40px]">
                {{ $t('COLOR_CHANGE_CONFIRM_COPY') }}
            </div>
            <div
                v-if="hasPendingColorChange"
                class="border-b border-gray-DFDFDF -mx-[20px] lg:-mx-[40px] px-[20px] lg:px-[40px]"
            >
                <div
                    :class="[
                        'flex flex-col items-center gap-[16px] font-oceanside font-semibold',
                        'border-b border-gray-DFDFDF -mx-[20px] lg:-mx-[40px] py-[24px]',
                    ]"
                >
                    <div
                        class="w-[52px] h-[52px] rounded-full flex-shrink-0 relative ring-1 ring-black ring-offset-4"
                        :style="{ backgroundColor: pendingColorChange.toColor || 'transparent' }"
                    >
                        <div
                            :class="[
                                'flex items-center justify-center rounded-full absolute pointer-events-none checkbox-visual',
                                'rounded-full w-6 h-6 bg-black bottom-[-8px] right-[-8px] border border-gray-light',
                            ]"
                        >
                            <img
                                src="../assets/images/icons/check-white.svg"
                                width="12"
                                height="12"
                                class="pointer-events-none absolute"
                            />
                        </div>
                    </div>
                    <div class="flex">
                        <span>{{ $t('NEW_SELECTION') }}&nbsp;</span>
                        <span>{{ pendingColorChange.toName }}</span>
                    </div>
                </div>

                <div class="flex flex-col w-full justify-center items-center pt-[24px] pb-[16px]">
                    <div class="flex items-center font-semibold">
                        {{ $t('YOUR_PALETTE') }}
                    </div>
                    <div class="flex items-center">
                        {{ $t('SELECT_A_COLOR_TO_REPLACE') }}
                    </div>
                    <step-options-my-palette
                        ref="replacementPalette"
                        :selected-color="canConfirmReplacement ? pendingColorChange.fromColor : null"
                        :disabled-values="disabledReplacementValues"
                        :show-label="false"
                        @updateColorIsSelected="handleUpdateReplacementSlot"
                    />
                </div>
            </div>
            <template v-if="hasPendingColorChange && canConfirmReplacement">
                <div class="mt-[28px]">
                    <div v-for="categoryGroup in categorizedAssignments"
                        :key="categoryGroup.category"
                        class="mb-6"
                    >
                        <div class="flex items-center gap-4 mb-2 font-semibold">
                            <div class="leading-none font-oceanside">
                                {{ categoryGroup.category }}
                            </div>
                            <div class="flex-1"></div>
                            <div class="flex -mr-4">
                                <div class="leading-none font-oceanside w-[120px] text-center">
                                    {{ $t('COLOR_CHANGE_CONFIRM_CHANGE_FROM') }}
                                </div>
                                <div class="leading-none font-oceanside w-[120px] text-center">
                                    {{ $t('COLOR_CHANGE_CONFIRM_CHANGE_TO') }}
                                </div>
                            </div>
                        </div>

                        <div class="border-t border-gray-DFDFDF mb-3"></div>

                        <div
                            v-for="(assignment, index) in categoryGroup.assignments"
                            :key="`${assignment.group}-${assignment.optionName}`"
                            :class="[
                                'flex items-center gap-2 py-[16px] px-4',
                                index % 2 === 0 ? 'bg-gray-F6F6F6' : 'bg-white',
                            ]"
                        >
                            <div class="leading-none font-oceanside flex-1">
                                {{ getAssignmentLabel(assignment) }}
                            </div>

                            <div
                                class="w-[32px] h-[32px] rounded-full border border-gray-DFDFDF flex-shrink-0"
                                :style="{ backgroundColor: pendingColorChange.fromColor || 'transparent' }"
                            />

                            <img src="../assets/images/icons/right-arrow.svg" class="w-6 h-6 flex-shrink-0 mx-4" alt="" />

                            <div
                                class="w-[32px] h-[32px] rounded-full border border-gray-DFDFDF flex-shrink-0"
                                :style="{ backgroundColor: pendingColorChange.toColor || 'transparent' }"
                            />
                        </div>
                    </div>

                    <p v-if="assignments.length === 0" class="text-[16px] text-center">
                        {{ $t('COLOR_CHANGE_CONFIRM_NO_ASSIGNMENTS') }}
                    </p>
                </div>
            </template>

        </div>

        <div v-if="hasPendingColorChange" class="border-t border-gray-DFDFDF px-[20px] lg:px-[40px] py-[16px] bg-white">
            <div class="flex items-center gap-4 font-semibold">
                <button
                    type="button"
                    :class="[
                        'h-[40px] rounded-full border-2 border-red text-red leading-none font-oceanside flex-1',
                        'hover:opacity-90',
                        'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                    ]"
                    @click="handleCancel"
                >
                    {{ $t('COLOR_CHANGE_CONFIRM_CANCEL') }}
                </button>
                <button
                    type="button"
                    :class="[
                        'h-[40px] rounded-full border border-red bg-red text-white leading-none font-oceanside flex-1',
                        'hover:opacity-90',
                        'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                        { 'opacity-25': !canConfirmReplacement },
                    ]"
                    :disabled="!canConfirmReplacement"
                    @click="handleConfirm"
                >
                    {{ $t('COLOR_CHANGE_CONFIRM_CONFIRM') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import getPlacementLabel from '../helpers/placementlabel';
    import StepOptionsMyPalette from './StepOptionsMyPalette.vue';

    export default {
        name: 'ColorChangeConfirm',

        mixins: [allStores],

        components: {
            StepOptionsMyPalette,
        },

        props: {
            payload: {
                type: Object,
                default: null,
            },
        },

        computed: {
            livePayload() {
                if (!this.customizerStore?.uiPendingStepSubOptionColorChange) {
                    return this.payload;
                }

                return this.customizerStore.getColorChangeConfirmPayload(this.payload?.meta || null);
            },

            isFocusLockEnabled() {
                return this.appStore?.activeNestedPanel?.panelId === 'colorChangeConfirm';
            },

            pendingColorChange() {
                return this.livePayload?.pendingColorChange || null;
            },

            hasPendingColorChange() {
                return !!this.pendingColorChange;
            },

            assignments() {
                return this.pendingColorChange?.assignments || [];
            },

            disabledReplacementValues() {
                const palette = this.customizerStore.masterPalette || [];
                const incomingValue = this.pendingColorChange?.toValue;
                const incomingColor = palette.find((color) => (
                    color.value === incomingValue || color.color === incomingValue
                ));

                if (!incomingColor?.translucent) {
                    return [];
                }

                const paletteValues = this.customizerStore.paletteSwatchSlots || [];
                const locations = this.livePayload?.options || [];
                const controller = this.customizerStore.viewController;

                return paletteValues.filter((replacementValue) => {
                    const assignedLocations = locations.filter((location) => (
                        location.activeSlotValue === replacementValue
                    ));

                    return assignedLocations.some((location) => {
                        const placement = controller.findPlacement(location.name);
                        const allowsIncomingColor = placement?.components?.some((component) => (
                            !component.placeholder
                            && component.custom?.['v-id'] === incomingColor.id
                        ));

                        return !allowsIncomingColor;
                    });
                });
            },

            canConfirmReplacement() {
                return !!this.pendingColorChange?.fromValue
                    && !this.disabledReplacementValues.includes(this.pendingColorChange.fromValue);
            },

            categorizedAssignments() {
                const categoryOrder = ['Sole', 'Back', 'Top', 'Face', 'Face/Sole'];
                const groups = this.assignments.reduce((acc, assignment) => {
                    const category = assignment.category || 'Face/Sole';
                    const existing = acc[category] || [];

                    return {
                        ...acc,
                        [category]: [...existing, assignment],
                    };
                }, {});

                return Object.keys(groups)
                    .sort((a, b) => {
                        const aIndex = categoryOrder.indexOf(a);
                        const bIndex = categoryOrder.indexOf(b);
                        const normalizedA = aIndex === -1 ? 999 : aIndex;
                        const normalizedB = bIndex === -1 ? 999 : bIndex;

                        if (normalizedA !== normalizedB) {
                            return normalizedA - normalizedB;
                        }

                        return a.localeCompare(b);
                    })
                    .map((category) => ({
                        category,
                        assignments: groups[category],
                    }));
            },

            formattedPayload() {
                if (!this.payload) {
                    return '';
                }

                try {
                    return JSON.stringify(this.livePayload, null, 2);
                } catch (error) {
                    return '';
                }
            },
        },

        watch: {
            isFocusLockEnabled: {
                immediate: true,
                handler(isEnabled) {
                    if (!isEnabled || !this.hasPendingColorChange) {
                        return;
                    }

                    this.focusFirstReplacementSwatch();
                },
            },

            hasPendingColorChange(hasPendingColorChange) {
                if (!this.isFocusLockEnabled || !hasPendingColorChange) {
                    return;
                }

                this.focusFirstReplacementSwatch();
            },
        },

        methods: {
            focusFirstReplacementSwatch() {
                this.$nextTick(() => {
                    window.setTimeout(() => {
                        const paletteRoot = this.$refs.replacementPalette?.$el;
                        const firstSwatch = paletteRoot?.querySelector('button:not([disabled])');

                        firstSwatch?.focus();
                    }, 0);
                });
            },

            closePanel() {
                if (this.hasPendingColorChange) {
                    this.customizerStore.cancelStepSubOptionColorChange();
                    return;
                }

                this.appStore.closeNestedPanel();
            },

            handleCancel() {
                this.customizerStore.cancelStepSubOptionColorChange();
            },

            handleConfirm() {
                if (!this.canConfirmReplacement) {
                    return;
                }

                this.customizerStore.confirmStepSubOptionColorChange({
                    isReplacement: this.assignments?.length > 0,
                });
            },

            handleUpdateReplacementSlot(option) {
                const nextFromValue = option?.value || option?.color || null;

                if (!nextFromValue) {
                    return;
                }

                this.customizerStore.updatePendingStepSubOptionColorChangeFromValue(nextFromValue);
            },

            getPaintfillOptionLabel(optionName) {
                return getPlacementLabel(
                    this.customizerStore.viewController,
                    optionName,
                    this.$i18n,
                ) || this.formatOptionName(optionName);
            },

            getAssignmentLabel(assignment) {
                if (
                    assignment.group === 'Front Paintfill Weights'
                    || assignment.group === 'Front Paintfill Logos'
                ) {
                    return this.getPaintfillOptionLabel(assignment.optionName);
                }

                return this.formatOptionName(assignment.optionName);
            },

            formatOptionName(optionName) {
                let formattedName = optionName;
                // remove 'Color' from the end of the option name
                formattedName = formattedName.replace('Color', '');
                // convert CamelCase to space separated words
                formattedName = formattedName.replace(/([A-Z])/g, ' $1').trim();

                if (formattedName === 'Stampings Line1') {
                    return 'Initials';
                }

                return formattedName;
            },
        },
    };
</script>
